import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, Download, FileText, XCircle } from 'lucide-react';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { Textarea } from '@/components/textarea';
import { StatusPill } from '@/components/status-dot';
import { SkeletonList } from '@/components/skeleton';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  useApproveProposal,
  useDeclineProposal,
  useDeferProposal,
  usePortalApprovable,
} from '@/lib/portal-approvables';
import { useHasPermission } from '@/lib/permissions';
import { dollars, formatDate, formatDateTime, formatStatus } from '@/lib/format';
import { statusTone } from '@/lib/approvable-status';

export function PortalApprovableDetail() {
  const { id } = useParams();
  const approvableId = id ? Number(id) : undefined;
  const { data: item, isLoading } = usePortalApprovable(approvableId);
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const canAct = useHasPermission('proposals.act');

  const approve = useApproveProposal(approvableId ?? 0);
  const decline = useDeclineProposal(approvableId ?? 0);
  const defer = useDeferProposal(approvableId ?? 0);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineComment, setDeclineComment] = useState('');
  const [deferOpen, setDeferOpen] = useState(false);
  const [deferComment, setDeferComment] = useState('');

  if (isLoading || !item) {
    return <SkeletonList rows={5} />;
  }

  const canRespond = item.is_open && canAct;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <button
        type="button"
        onClick={() => navigate('/proposals')}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]"
      >
        <ArrowLeft className="size-4" /> Back to proposals
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{item.title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[var(--color-foreground-muted)]">
            <StatusPill tone={statusTone(item.status)}>{formatStatus(item.status)}</StatusPill>
            {item.sent_at ? <span>Sent {formatDate(item.sent_at)}</span> : null}
            {item.expires_at ? <span>· Expires {formatDate(item.expires_at)}</span> : null}
          </div>
        </div>
        {item.total_cents != null ? (
          <div className="text-right">
            <div className="text-2xl font-semibold tabular-nums">{dollars(item.total_cents)}</div>
            {item.deposit_cents ? (
              <div className="text-xs text-[var(--color-foreground-muted)]">
                {dollars(item.deposit_cents)} deposit
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {item.description ? (
        <p className="whitespace-pre-line text-sm text-[var(--color-foreground)]">{item.description}</p>
      ) : null}

      {/* Actions */}
      {canRespond || item.receipt_url ? (
        <div className="flex flex-wrap items-center gap-2">
          {canRespond ? (
            <>
              <Button
                onClick={async () => {
                  const ok = await confirm({
                    title: `Accept "${item.title}"?`,
                    body: 'This lets the Mehrutherm team know you approve and want to move forward.',
                    confirmLabel: 'Accept proposal',
                  });
                  if (ok) {
                    await approve.mutateAsync(undefined);
                    toast.success('Proposal accepted', 'The Mehrutherm team has been notified.');
                  }
                }}
              >
                <CheckCircle2 className="size-4" /> Accept
              </Button>
              <Button variant="secondary" onClick={() => setDeferOpen(true)}>
                <Clock className="size-4" /> Maybe later
              </Button>
              <Button variant="secondary" onClick={() => setDeclineOpen(true)}>
                <XCircle className="size-4" /> Decline
              </Button>
            </>
          ) : null}
          {item.receipt_url ? (
            <a href={item.receipt_url} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary">
                <Download className="size-4" /> Receipt
              </Button>
            </a>
          ) : null}
        </div>
      ) : null}

      {/* Document */}
      {item.file_url ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <FileText className="size-4 text-[var(--color-foreground-muted)]" />
                <h2 className="text-sm font-semibold">{item.file_name ?? 'Document'}</h2>
              </div>
              <a
                href={item.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[var(--color-primary)] hover:underline"
              >
                Open in new tab
              </a>
            </div>
          </CardHeader>
          <CardBody className="!p-0">
            <iframe title={item.title} src={item.file_url} className="h-[600px] w-full" />
          </CardBody>
        </Card>
      ) : null}

      {/* History */}
      {item.approvals.length > 0 ? (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">History</h2>
          </CardHeader>
          <CardBody className="!p-0">
            <ul className="divide-y divide-[var(--color-border)]">
              {item.approvals.map((a) => (
                <li key={a.id} className="px-5 py-3 text-sm">
                  <span className="font-medium capitalize">{a.action}</span>
                  {a.signed_name ? <span> by {a.signed_name}</span> : null}
                  <span className="text-[var(--color-foreground-muted)]"> · {formatDateTime(a.created_at)}</span>
                  {a.comment ? (
                    <p className="mt-0.5 text-[var(--color-foreground-muted)]">“{a.comment}”</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {/* Decline dialog */}
      <Dialog open={declineOpen} onClose={() => setDeclineOpen(false)} title={`Decline "${item.title}"?`}>
        <p className="text-sm text-[var(--color-foreground-muted)]">
          Let the Mehrutherm team know why, if you'd like. This is optional.
        </p>
        <Textarea
          value={declineComment}
          onChange={(e) => setDeclineComment(e.target.value)}
          placeholder="Reason (optional)"
          className="mt-3"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeclineOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              await decline.mutateAsync({ comment: declineComment || undefined });
              setDeclineOpen(false);
              setDeclineComment('');
              toast.success('Proposal declined', 'The Mehrutherm team has been notified.');
            }}
          >
            Decline proposal
          </Button>
        </div>
      </Dialog>

      {/* Maybe later dialog */}
      <Dialog open={deferOpen} onClose={() => setDeferOpen(false)} title={`Maybe later on "${item.title}"?`}>
        <p className="text-sm text-[var(--color-foreground-muted)]">
          We'll set this aside and can pick it back up whenever the timing is better. Add a note if you'd like.
        </p>
        <Textarea
          value={deferComment}
          onChange={(e) => setDeferComment(e.target.value)}
          placeholder="Note (optional)"
          className="mt-3"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeferOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await defer.mutateAsync({ comment: deferComment || undefined });
              setDeferOpen(false);
              setDeferComment('');
              toast.success('Saved for later', 'The Mehrutherm team has been notified.');
            }}
          >
            Maybe later
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
